// @flow

// Libs
import React, { Component } from 'react'
import _size from 'lodash/size'
import _map from 'lodash/map'
import _filter from 'lodash/filter'
import _indexOf from 'lodash/indexOf'

// Components
import createControlledComponent from '../components/ControlledDataTable'
import ListItems from 'fc-lists/src/components/ListItems'

// Types
import type {
  ComposeDataTableOptions,
} from '../../types'

type Props<R> = {
  // Composed Props
  perPage: number,
  page: number,
  pages: Array<Array<R>>,
  pageRecords: Array<R>,
  max: number,
  sortDesc: boolean,
  sortBy: ?string,
  filters: {[string]: string},
  selectedRecordIds: Array<number>,
  mobile: boolean,

  // Actions
  actions: {
    onPaginate: (page: number) => any, // eslint-disable-line react/no-unused-prop-types
    onSort: Function,
    onFilter: Function,
    onSelect: Function, // eslint-disable-line react/no-unused-prop-types
    onSelectAll: Function, // eslint-disable-line react/no-unused-prop-types
  },

  // Maybe Props
  columns?: Array<Object>,
  renderer?: { [string]: () => React.Element<any>},
  tools?: Array<React.Element<any>>,
}

// Exports
export default function composeClass<M, R: { id: number }> ({ // eslint-disable-line no-unused-vars
  options,
  container,
  }: {
  options: ComposeDataTableOptions,
  container: *,
}): * {
  //
  const ControlledDataTable = createControlledComponent(options)

  class DataTable extends Component {
    props: Props<R>
    onPaginateFirst: Function
    onPaginateLeft: Function
    onPaginateRight: Function
    onPaginateLast: Function
    onClearSelection: Function
    onSelect: Function
    onSelectAll: Function

    constructor (props: Props<R>) {
      super(props)

      if (options.allowPagination) {
        this.onPaginateFirst = this.onPaginateFirst.bind(this)
        this.onPaginateLeft = this.onPaginateLeft.bind(this)
        this.onPaginateRight = this.onPaginateRight.bind(this)
        this.onPaginateLast = this.onPaginateLast.bind(this)
      }

      if (options.allowSelecting) {
        this.onSelect = this.onSelect.bind(this)
        this.onSelectAll = this.onSelectAll.bind(this)
        this.onClearSelection = this.onClearSelection.bind(this)
      }
    }

    render (): React.Element<any> {
      const props = {}
      const start = (this.props.page * this.props.perPage) + 1
      const calculatedEnd = start + this.props.perPage - 1
      const end = calculatedEnd > this.props.max ? this.props.max : calculatedEnd

      if (options.allowPagination) {
        props.onPaginateFirst = this.onPaginateFirst
        props.onPaginateLeft = this.onPaginateLeft
        props.onPaginateRight = this.onPaginateRight
        props.onPaginateLast = this.onPaginateLast
      }

      if (options.allowSelecting) {
        props.selectedRecords = this.props.selectedRecordIds
        props.onSelect = this.onSelect
        props.onSelectAll = this.onSelectAll
        props.onClearSelection = this.onClearSelection
      }

      if (options.allowFiltering) {
        props.onFilter = this.props.actions.onFilter
        props.filters = this.props.filters
      }

      if (options.allowSorting) {
        props.onSort = this.props.actions.onSort
        props.sortBy = this.props.sortBy
        props.asc = !this.props.sortDesc
      }

      let Comp = this.props.mobile ? ListItems : ControlledDataTable

      return (
        <Comp
          {...this.props}
          columns={this.props.columns}
          renderer={this.props.renderer}
          tools={this.props.tools}
          perPage={this.props.perPage}
          page={this.props.page}
          pages={this.props.pages}
          max={this.props.max}
          start={start}
          end={end}
          {...props}
          records={this.props.pageRecords}
        />
      )
    }
  }

  if (options.allowPagination) {
    DataTable.prototype.onPaginateFirst = function onPaginateFirst (e: Event) {
      e && e.preventDefault()
      this.props.actions.onPaginate(0)
    }

    DataTable.prototype.onPaginateLeft = function onPaginateLeft (e: Event) {
      e && e.preventDefault()
      if (this.props.page > 0) {
        this.props.actions.onPaginate(this.props.page - 1)
      }
    }

    DataTable.prototype.onPaginateRight = function onPaginateRight (e: Event) {
      e && e.preventDefault()
      if (this.props.page < _size(this.props.pages) - 1) {
        this.props.actions.onPaginate(this.props.page + 1)
      }
    }

    DataTable.prototype.onPaginateLast = function onPaginateLast (e: Event) {
      e && e.preventDefault()
      if (this.props.page < _size(this.props.pages) - 1) {
        this.props.actions.onPaginate(_size(this.props.pages) - 1)
      }
    }
  }

  if (options.allowSelecting) {
    DataTable.prototype.onSelect = function onSelect (recordId: number) {
      this.props.actions.onSelect([recordId])
    }

    DataTable.prototype.onSelectAll = function onSelectAll () {
      const unselectedRecords: Array<R> = _filter(this.props.pageRecords, (record: R): boolean => {
        return _indexOf(this.props.selectedRecordIds, record.id) < 0
      })

      if (!_size(unselectedRecords)) {
        this.props.actions.onSelect(this.props.selectedRecordIds)
      } else {
        const unselectedRecordIds = _map(unselectedRecords, 'id')
        this.props.actions.onSelect(unselectedRecordIds)
      }
    }

    DataTable.prototype.onClearSelection = function onClearSelection () {
      this.props.actions.onSelect(this.props.selectedRecordIds)
    }
  }

  return container(DataTable)
}
